import { toast } from "vue-sonner";
import { useI18n } from "vue-i18n";
import {
  MAX_SOURCE_FILE_SIZE_BYTES,
  SUPPORTED_MEDIA_TYPES_LABEL,
  buildMaxSourceFileSizeLabel,
} from "~/utils/media-subtitle-workflow";
import type { UploadErrorMapper } from "~/utils/media-upload";
import { MIN_CLIP_SECONDS } from "~/utils/clip-range";

/**
 * User-facing copy for video-to-subtitle workflow errors (server messages,
 * upload failures, platform error codes). Shared by the single-job workflow
 * and the batch queue so both show the same wording.
 */
export const useMediaWorkflowErrors = () => {
  const { t } = useI18n();
  const { notifyInsufficientCredits } = useCreditShortfall();
  const isDevelopment = import.meta.dev;
  const maxSourceFileSizeLabel = buildMaxSourceFileSizeLabel(MAX_SOURCE_FILE_SIZE_BYTES);

  const tt = (key: string, fallback: string, params?: Record<string, unknown>) => {
    const translated = t(key, params as any);
    return translated === key ? fallback.replace(/\{(\w+)\}/g, (_, token) => `${params?.[token] ?? ""}`) : translated;
  };

  function getSupportedMediaErrorMessage() {
    return tt(
      "pages.audioToSubtitle.errors.mediaOnly",
      "Only {type} files are supported.",
      { type: SUPPORTED_MEDIA_TYPES_LABEL }
    );
  }

  function getGenericWorkflowErrorMessage() {
    return tt(
      "pages.audioToSubtitle.errors.failed",
      "Something went wrong while creating the subtitle. Please try again."
    );
  }

  function getNoSpeechErrorMessage() {
    return tt(
      "pages.audioToSubtitle.errors.noSpeechDetected",
      "No speech was detected in this video, so no subtitle could be created. Your credits were not charged."
    );
  }

  function getFileTooLargeErrorMessage() {
    return tt(
      "pages.audioToSubtitle.errors.fileTooLarge",
      "This media file is too large. Please upload a file smaller than {size}.",
      { size: maxSourceFileSizeLabel }
    );
  }

  function getSourceUnavailableErrorMessage() {
    return tt(
      "pages.audioToSubtitle.errors.sourceUnavailable",
      "This YouTube video cannot be downloaded. It may be private, age-restricted, region-locked or removed. Your credits were not charged."
    );
  }

  function getNotEnoughCreditsMessage() {
    return t("pages.audioToSubtitle.errors.notEnoughCredits");
  }

  function getFriendlyKnownErrorMessage(message: string) {
    const normalizedMessage = message.trim();

    if (!normalizedMessage) {
      return "";
    }

    if (
      normalizedMessage.includes("too large to process") ||
      normalizedMessage.includes("FILE_TOO_LARGE") ||
      normalizedMessage.includes("This media file is too large")
    ) {
      return getFileTooLargeErrorMessage();
    }

    if (
      normalizedMessage.includes("No speech was detected") ||
      normalizedMessage.includes("NO_SPEECH_DETECTED")
    ) {
      return getNoSpeechErrorMessage();
    }

    if (
      normalizedMessage.includes("cannot be downloaded") ||
      normalizedMessage.includes("SOURCE_UNAVAILABLE")
    ) {
      return getSourceUnavailableErrorMessage();
    }

    if (
      normalizedMessage.includes("Only audio or video uploads are supported") ||
      normalizedMessage.includes("Unsupported Media Type")
    ) {
      return getSupportedMediaErrorMessage();
    }

    // Clip range refusals (server/utils/video-subtitle-clip.ts).
    if (normalizedMessage.includes("The video length is unknown, so a time range")) {
      return t("clipRange.errorNeedsDuration");
    }

    if (normalizedMessage.includes("The time range must be at least")) {
      return t("clipRange.errorInvalid", { seconds: MIN_CLIP_SECONDS });
    }

    if (normalizedMessage.includes("Missing uploaded file")) {
      return t("pages.audioToSubtitle.errors.missingFile");
    }

    if (normalizedMessage.includes("Pro membership required")) {
      return t("pages.audioToSubtitle.errors.proRequired");
    }

    // Checked before "Not enough credits": a new job is refused while an
    // unpaid result is waiting to be claimed (server assertNoUnpaidPlatformJob).
    if (normalizedMessage.includes("Unpaid result pending")) {
      return t("pages.audioToSubtitle.errors.unpaidResultPending");
    }

    if (normalizedMessage.includes("Not enough credits")) {
      return getNotEnoughCreditsMessage();
    }

    if (
      normalizedMessage.includes("Maximum number of running container instances exceeded") ||
      normalizedMessage.includes("There is no Container instance available at this time")
    ) {
      return tt(
        "pages.audioToSubtitle.errors.conversionBusy",
        "The video conversion service is busy right now. Please try again in a moment."
      );
    }

    if (
      normalizedMessage.includes("Unable to load transcript chunk") ||
      normalizedMessage.includes("Unable to transcribe audio") ||
      normalizedMessage.includes("TRANSCRIPT_FAILED")
    ) {
      return tt(
        "pages.audioToSubtitle.errors.transcriptFailed",
        "We could not create a transcript for this media right now. Please try again in a moment."
      );
    }

    return "";
  }

  function isNotEnoughCreditsMessage(message: string) {
    return message === getNotEnoughCreditsMessage();
  }

  // Workflow errors arrive already mapped to friendly copy; the credit one gets
  // the toast with a "Purchase credits" action instead of a plain error.
  function toastWorkflowError(message: string) {
    if (isNotEnoughCreditsMessage(message)) {
      notifyInsufficientCredits({ feature: "media_subtitle", message });
    } else {
      toast.error(message);
    }
  }

  function getPublicWorkflowErrorMessage(message: string) {
    const knownMessage = getFriendlyKnownErrorMessage(message);
    if (knownMessage) {
      return knownMessage;
    }

    return isDevelopment ? message.trim() || getGenericWorkflowErrorMessage() : getGenericWorkflowErrorMessage();
  }

  function getFriendlyTranscriptErrorMessage(status: number, rawText: string) {
    const fallbackMessage = isDevelopment
      ? t("pages.audioToSubtitle.errors.uploadFailedStatus", { status })
      : getGenericWorkflowErrorMessage();

    if (!rawText) {
      return fallbackMessage;
    }

    let parsedMessage = "";

    try {
      const parsed = JSON.parse(rawText) as {
        message?: string;
        statusMessage?: string;
        data?: { message?: string; statusMessage?: string };
      };
      parsedMessage =
        parsed?.message ||
        parsed?.statusMessage ||
        parsed?.data?.message ||
        parsed?.data?.statusMessage ||
        "";
    } catch {
      parsedMessage = rawText;
    }

    const normalizedMessage = parsedMessage.trim();

    if (!normalizedMessage) {
      return fallbackMessage;
    }

    return getPublicWorkflowErrorMessage(normalizedMessage);
  }

  async function readErrorMessage(response: Response) {
    const errorText = await response.text().catch(() => "");
    return getFriendlyTranscriptErrorMessage(response.status, errorText);
  }

  /** Friendly copy for a thrown error (safe messages pass through). */
  function getErrorMessage(error: unknown) {
    return error instanceof Error ? error.message : t("pages.audioToSubtitle.errors.failed");
  }

  const uploadErrors: UploadErrorMapper = {
    uploadFailed: () => tt("pages.audioToSubtitle.errors.uploadFailed", "Unable to upload the media file."),
    fromText: (rawText: string) => getPublicWorkflowErrorMessage(rawText),
    fromResponse: readErrorMessage,
  };

  return {
    tt,
    maxSourceFileSizeLabel,
    getSupportedMediaErrorMessage,
    getGenericWorkflowErrorMessage,
    getNoSpeechErrorMessage,
    getFileTooLargeErrorMessage,
    getNotEnoughCreditsMessage,
    getFriendlyKnownErrorMessage,
    getPublicWorkflowErrorMessage,
    getFriendlyTranscriptErrorMessage,
    isNotEnoughCreditsMessage,
    readErrorMessage,
    toastWorkflowError,
    getErrorMessage,
    uploadErrors,
  };
};
